// OTP оруулах хэсэг [OTP verification]

import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Loader from '@/components/ui/loader'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import { sendOtp, verifyOtp } from '@/services/mobi-api/confirmation'
import useIntl from '@/utils/intl'
import { Divider } from '@mui/material'
import { FunctionComponent, useEffect, useState } from 'react'
import OTPInput from 'react-otp-input'
import {
  normalizeTrackingText,
  trackNewNumberEvent
} from '@/utils/newNumberFlowTracking'

type Channel = 'sms' | 'email' | 'app'

interface Props {
  regno: string
  channel: Channel
  onBack?: () => void
  onContinue?: (data: {
    verified: boolean
    otpCode: string
    regno: string
    channel: Channel
    verifyResponse?: unknown
  }) => void
}

const RESEND_SECONDS = 30
const OTP_LENGTH = 6

const Step4: FunctionComponent<Props> = ({ regno, channel, onContinue }) => {
  const { translate } = useIntl()
  const { locale } = useLanguage()
  const [seconds, setSeconds] = useState(RESEND_SECONDS)
  const [otp, setOtp] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [invalidOtp, setInvalidOtp] = useState(false)
  const { showError } = useToast()
  const [loading, setLoading] = useState(false)
  const [isKeyboardFocused, setKeyboardFocused] = useState(false)

  const getScrollableParent = (el: HTMLElement): HTMLElement | null => {
    let parent = el.parentElement

    while (parent != null) {
      const overflowY = window.getComputedStyle(parent).overflowY
      if (
        /(auto|scroll|overlay)/.test(overflowY) &&
        parent.scrollHeight > parent.clientHeight
      ) {
        return parent
      }
      parent = parent.parentElement
    }

    return null
  }

  const scrollInputIntoView = (el: HTMLInputElement | null): void => {
    if (el == null || typeof window === 'undefined') return

    const scroll = (): void => {
      const fallbackKeyboardTop = Math.round(window.innerHeight * 0.58)
      const keyboardTop = Math.min(
        window.visualViewport?.height ?? fallbackKeyboardTop,
        fallbackKeyboardTop
      )
      const rect = el.getBoundingClientRect()
      const overlap = rect.bottom - keyboardTop + 120

      if (overlap <= 0) return

      const scrollParent = getScrollableParent(el)
      if (scrollParent != null) {
        scrollParent.scrollBy({ top: overlap, behavior: 'smooth' })
        return
      }

      window.scrollBy({ top: overlap, behavior: 'smooth' })
    }

    setKeyboardFocused(true)
    window.setTimeout(scroll, 100)
    window.setTimeout(scroll, 350)
    window.setTimeout(scroll, 700)
  }

  const handleVerify = async (): Promise<void> => {
    if (otp.length !== OTP_LENGTH) { console.log('otp clicked', otp) }
    setInvalidOtp(false)
    try {
      trackNewNumberEvent('click_cta', {
        step_name: 'identity_verification',
        button_name: 'confirm_otp'
      })
      const res: any = await verifyOtp({ regno, otp })
      if (res.success === true) {
        onContinue?.({ verified: true, otpCode: otp, regno, channel, verifyResponse: res })
      } else {
        const error: any = res
        const errorCode = error?.errorCode
        switch (errorCode) {
          case 996:
            trackNewNumberEvent('verification_fail', {
              step_name: 'identity_verification',
              method: 'emongolia',
              error_reason: 'invalid_otp'
            })
            setInvalidOtp(true)
            setError('ТАН код буруу байна')
            showError('ТАН код буруу байна')
            break
          default: {
            const errorRes = res as { cause?: { messageMn?: string, messageEn?: string }, error?: string }
            const msg =
              (locale === 'en'
                ? errorRes?.cause?.messageEn
                : errorRes?.cause?.messageMn) ??
              errorRes?.error ??
              translate('error')
            trackNewNumberEvent('verification_fail', {
              step_name: 'identity_verification',
              method: 'emongolia',
              error_reason: normalizeTrackingText(msg)
            })
            setError(msg)
            showError(msg)
          }
        }
      }
    } catch (e) {
      const msg = translate('request.failed')
      trackNewNumberEvent('verification_fail', {
        step_name: 'identity_verification',
        method: 'emongolia',
        error_reason: 'verify_otp_failed'
      })
      setError(msg)
      showError(msg)
    }
  }

  const handleResend = async (): Promise<void> => {
    trackNewNumberEvent('retry_action', {
      step_name: 'identity_verification',
      action_name: 'verification_otp'
    })
    setInvalidOtp(false)
    setLoading(true)
    setOtp('')
    setError(null)
    try {
      await sendOtp({ regno, channel })
    } catch (e) {
    } finally {
      setLoading(false)
    }
    setSeconds(RESEND_SECONDS)
  }

  const handleOtpChange = (next: string): void => {
    console.log('otp changed', invalidOtp)
    setOtp(next)
    setInvalidOtp(false)
  }

  useEffect(() => {
    setSeconds(RESEND_SECONDS)
    setOtp('')
    setError(null)
    setInvalidOtp(false)
  }, [regno, channel])

  useEffect(() => {
    if (seconds <= 0) return
    const id = setInterval(() => {
      setSeconds((prev) => prev - 1)
    }, 1000)
    return () => clearInterval(id)
  }, [seconds])

  return (
    <div
      className='border border-surfaceHigh bg-surfaceMid rounded-xl text-center p-8'
      aria-busy={loading}
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: isKeyboardFocused ? '34vh' : 'env(safe-area-inset-bottom)'
      }}
    >
      {loading && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm'>
          <Loader />
        </div>
      )}
      <p className='text-textHigh font-extrabold text-xl'>
        {translate('verify.verify')}
      </p>
      <p className='text-textMid text-center'>
        {channel === 'sms' && translate('verify.phoneNumberTest')}
        {channel === 'email' && translate('verify.emailTest')}
        {channel === 'app' && translate('verify.appTest')}
      </p>

      <div
        className='w-full flex flex-col items-center gap-4 mt-4 min-h-[180px]'
      >
        <OTPInput
          value={otp}
          onChange={handleOtpChange}
          numInputs={OTP_LENGTH}
          renderSeparator={<Divider orientation='vertical' />}
          renderInput={(props: any) => (
            <input
              {...props}
              data-emongolia-otp='true'
              onFocus={(e) => {
                props.onFocus?.(e)
                scrollInputIntoView(e.currentTarget)
              }}
              onBlur={(e) => {
                props.onBlur?.(e)
                window.setTimeout(() => {
                  if (document.activeElement?.getAttribute('data-emongolia-otp') === 'true') {
                    return
                  }
                  setKeyboardFocused(false)
                }, 100)
              }}
              placeholder='*'
              className={[
                'w-[40px] h-[40px] sm:w-[45px] sm:h-[45px] rounded-lg text-center font-mono font-bold text-[16px] sm:text-[18px] leading-[40px] sm:leading-[45px]',
                'bg-surfaceInverse border',
                invalidOtp ? 'text-red-600 border-red-600' : 'text-textHigh border-[#CFD3DB]',
                'focus:placeholder-transparent'
              ].join(' ')}
            />
          )}
          inputType='number'
          inputStyle={{
            width: '45px',
            height: '45px',
            fontSize: '18px',
            color: `${invalidOtp ? 'red' : 'textHigh'}`,
            fontWeight: '700',
            caretColor: 'blue',
            borderRadius: '8px',
            backgroundColor: 'surfaceInverse'
          }}
          containerStyle={{
            border: `${invalidOtp ? '1px solid red' : '1px solid #CFD3DB'}`,
            borderRadius: '8px',
            outline: 'none',
            backgroundColor: 'surfaceInverse'
          }}
        />
        {error !== null && (
          <p className='text-red-600 text-sm mt-1' role='alert'>
            {error}
          </p>
        )}
        <DarkButton
          type='outlined'
          onClick={() => { void handleResend() }}
          disabled={seconds > 0}
          classNames='w-full max-w-[280px] mx-auto bg-surfaceMid'
        >
          {seconds > 0
            ? `${translate('sim.tan.getagain')} (${seconds})`
            : translate('sim.tan.getagain')}
        </DarkButton>
      </div>

      <DarkButton
        disabled={otp.length !== OTP_LENGTH}
        fullwidth
        classNames='mt-6 bg-surface text-textHigh'
        onClick={() => { void handleVerify() }}
      >
        {translate('continue')}
      </DarkButton>

    </div>
  )
}
export default Step4
